import { BaseDAO } from './baseDAO';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConstantMan } from '../constantMan';
import { ApiHelper } from '../apiHelper';
import { HttpHeaders } from '@angular/common/http';
import * as moment from 'moment';
import { BucketUploadResponse } from '../../pure-functions/upload/image-upload-helper';

@Injectable()
export class UploadDAO extends BaseDAO<any> {
  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.FILE);
  }

  public uploadFile(
    file: Blob,
    filename: string,
    vesselId?: number,
    specificBucketPath?: string,
    contentDisposition?: 'attachment',
  ): Observable<any> {
    let url: string = this.apiHelper.getServiceName() + this.getResource();
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');

    let formData: FormData = new FormData();
    formData.append('file', file, filename);

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      formData,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (data: any) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public uploadFileDynamicPath(
    file: Blob,
    specificBucketPath: string,
    contentDisposition?: 'attachment',
  ): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/dynamic-path';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    if (specificBucketPath) {
      headers = headers.append('bucket-path', specificBucketPath);
    }
    if (contentDisposition) {
      headers = headers.append('content-disposition', contentDisposition);
    }

    let formData: FormData = new FormData();
    formData.append('file', file);

    return this.apiHelper.apiCall<any>(url, method, null, formData, headers);
  }

  public uploadWaterBodyImage(
    file: Blob,
    filename: string,
  ): Observable<BucketUploadResponse> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/body-of-water-image/';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Cache-Control', 'max-age=604800');

    let formData: FormData = new FormData();
    formData.append('file', file, filename);

    return this.apiHelper.apiCall<BucketUploadResponse>(
      url,
      method,
      null,
      formData,
      headers,
    );
  }

  public uploadVesselImage(
    file: Blob,
    filename: string,
    vesselId?: number,
  ): Observable<BucketUploadResponse> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/vessel-images/' +
      vesselId;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Cache-Control', 'max-age=604800');

    let formData: FormData = new FormData();
    formData.append('file', file, filename);

    return this.apiHelper.apiCall<BucketUploadResponse>(
      url,
      method,
      null,
      formData,
      headers,
    );
  }

  private fileToBlob(file: File): Promise<Blob> {
    return new Promise<Blob>((resolve) => {
      let reader = new FileReader();
      reader.onloadend = function () {
        let data = reader.result;
        resolve(new Blob([data], { type: file.type }));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  public uploadFileSecure(
    file: Blob,
    filename: string,
    userId?: number,
    s3path?: string,
    acl?: string,
  ): Observable<any> {
    let url: string;
    if (userId) {
      url =
        this.apiHelper.getServiceName() +
        this.getResource() +
        '/secure/' +
        userId;
    } else {
      url = this.apiHelper.getServiceName() + this.getResource() + '/secure';
    }
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    if (s3path) {
      headers = headers.append('s3path', s3path);
    }
    if (acl) {
      headers = headers.append('acl', acl);
    }

    let formData: FormData = new FormData();

    if (filename === 'image.jpg') {
      let temp = filename;
      filename = moment().unix().toString();
      filename += '.' + temp.split('.').pop();
    }

    return Observable.create((observer) => {
      formData.append('file', file, filename);

      let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
        url,
        method,
        null,
        formData,
        headers,
      );

      httpRequest.subscribe(
        (data: any) => {
          console.log('UPLOAD RESPONSE', data);
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public uploadCustomerGovId(file: Blob, filename: string): Observable<any> {
    let url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/user/gov/identification/';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('bucket-path', filename);

    let formData: FormData = new FormData();
    formData.append('file', file, filename);
    return this.apiHelper.apiCall<any>(url, method, null, formData, headers);
  }

  public uploadVesselInsurance(file: Blob, filename: string): Observable<any> {
    let url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/vessel/insurance/';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('bucket-path', filename);

    let formData: FormData = new FormData();
    formData.append('file', file, filename);
    return this.apiHelper.apiCall<any>(url, method, null, formData, headers);
  }

  public uploadVesselFileSecure(
    file: Blob,
    filename: string,
    userId?: number,
    s3path?: string,
    acl?: string,
  ): Observable<any> {
    let url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/secure-vessel/' +
      userId;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');

    if (s3path) {
      headers = headers.append('s3path', s3path);
    }
    if (acl) {
      headers = headers.append('acl', acl);
    }

    let formData: FormData = new FormData();

    if (filename === 'image.jpg') {
      let temp = filename;
      filename = moment().unix().toString();
      filename += '.' + temp.split('.').pop();
    }

    return Observable.create((observer) => {
      formData.append('file', file, filename);

      let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
        url,
        method,
        null,
        formData,
        headers,
      );

      httpRequest.subscribe(
        (data: any) => {
          console.log('UPLOAD RESPONSE', data);
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public uploadFileSeaService(
    file: File,
    vessel: number,
    userId?: number,
  ): Observable<any> {
    let url: string;
    if (userId) {
      url =
        this.apiHelper.getServiceName() +
        this.getResource() +
        '/seaService/' +
        vessel +
        '/' +
        userId;
    } else {
      url =
        this.apiHelper.getServiceName() +
        this.getResource() +
        '/seaService/' +
        vessel;
    }
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');

    let formData: FormData = new FormData();
    let filename = file.name.split('.')[0];
    if (file.name === 'image.jpg') {
      filename = moment().unix().toString();
    }
    filename += '.' + file.name.split('.').pop();

    return Observable.create((observer) => {
      let ftob: Promise<Blob> = this.fileToBlob(file);
      ftob.then((blob) => {
        formData.append('file', blob, filename);

        let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
          url,
          method,
          null,
          formData,
          headers,
        );

        httpRequest.subscribe(
          (data: any) => {
            observer.next(data);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
      });
    });
  }

  public getUploadedFiles(id?: number): Observable<any> {
    let url: string;
    if (id) {
      url = this.apiHelper.getServiceName() + this.getResource() + '/' + id;
    } else {
      url = this.apiHelper.getServiceName() + this.getResource();
    }
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      null,
      headers,
    );
    return Observable.create((observer) => {
      httpRequest.subscribe(
        (data: any) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public getUploadedVesselFiles(id?: number): Observable<any> {
    let url: string;
    if (id) {
      url =
        this.apiHelper.getServiceName() +
        this.getResource() +
        '/vessel-files/' +
        id;
    } else {
      url = this.apiHelper.getServiceName() + this.getResource();
    }
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      null,
      headers,
    );
    return Observable.create((observer) => {
      httpRequest.subscribe(
        (data: any) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public downloadFile(key: string, id?: number): Observable<any> {
    let url: string;
    if (id) {
      url =
        this.apiHelper.getServiceName() +
        this.getResource() +
        '/download/' +
        id;
    } else {
      url = this.apiHelper.getServiceName() + this.getResource() + '/download';
    }

    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      key: key,
    };

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
      true,
    );
    return Observable.create((observer) => {
      httpRequest.subscribe(
        (responseData: any) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public downloadFileWithKey(key: string): Observable<any> {
    let url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/download-with-key';
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      key: key,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public delete(
    filename: string,
    id?: number,
    altLocation?: string,
  ): Observable<any> {
    console.log('delete', filename, id, altLocation);
    let url: string;
    if (id) {
      url = this.apiHelper.getServiceName() + this.getResource() + '/' + id;
    } else {
      url = this.apiHelper.getServiceName() + this.getResource();
    }
    let method = 'DELETE';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      fname: filename,
      altlocation: altLocation,
    };
    console.log(data);

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
    );
    return Observable.create((observer) => {
      httpRequest.subscribe(
        (responseData: any) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public deleteVesselFile(
    filename: string,
    id?: number,
    altLocation?: string,
  ): Observable<any> {
    console.log('delete', filename, id, altLocation);
    let url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/vessel-files/' +
      id;
    let method = 'DELETE';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      fname: filename,
      altlocation: altLocation,
    };
    console.log(data);

    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
    );
    return Observable.create((observer) => {
      httpRequest.subscribe(
        (responseData: any) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  uploadForCaptainManageAdmin(
    file: Blob,
    filename: string,
    userId: number,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/capt-manage-charter-admin/' +
      userId;
    const method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('bucket-path', `licenses/${userId}`);

    let formData: FormData = new FormData();
    formData.append('file', file, filename);

    return this.apiHelper.apiCall<any>(url, method, null, formData, headers);
  }

  getArchivedSignedCA(charterId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/archived-signed-ca/' +
      charterId;
    const method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }
}

export interface ResourceSigner {
  getSignedResource(
    foreignKey: number | string,
    resourceKey: string,
  ): Observable<SignedResourceResponse>;
  adminGetSignedResource?(
    foreignKey: number | string,
    resourceKey: string,
  ): Observable<SignedResourceResponse>;
}
export interface SignedResourceResponse {
  signedRequest: string;
}
